import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { usersService } from '../../../lib/services';
import { useAuth } from '../../../contexts/AuthContext';

const UserManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { userProfile } = useAuth();

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' }
  ];

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await usersService?.getAll();
      if (fetchError) throw fetchError;
      setUsers(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = users?.filter(user => {
    const matchesSearch = !searchQuery ||
      user?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    const colors = {
      'super_admin': 'bg-error/10 text-error',
      'admin': 'bg-primary/10 text-primary',
      'editor': 'bg-success/10 text-success'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'editor': 'Editor'
    };
    return labels?.[role] || role;
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground';
  };

  const getUserInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2) || '?';
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            options={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="Filter by role"
          />
        </div>
        <Button
          variant="default"
          iconName="UserPlus"
          iconPosition="left"
          className="w-full sm:w-auto"
          onClick={() => console.log('Invite user')}
        >
          Invite User
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
          <span className="text-muted-foreground">Loading users...</span>
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 p-4 bg-error/10 border border-error/20 rounded-lg text-error">
          <Icon name="AlertCircle" size={16} />
          <span className="text-sm">{error}</span>
          <button onClick={loadUsers} className="ml-auto text-xs underline">Retry</button>
        </div>
      ) : (
        <>
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 md:px-6 py-3 text-xs md:text-sm font-semibold text-foreground">User</th>
                    <th className="text-left px-4 md:px-6 py-3 text-xs md:text-sm font-semibold text-foreground">Role</th>
                    <th className="text-left px-4 md:px-6 py-3 text-xs md:text-sm font-semibold text-foreground">Status</th>
                    <th className="text-left px-4 md:px-6 py-3 text-xs md:text-sm font-semibold text-foreground">Last Login</th>
                    <th className="text-left px-4 md:px-6 py-3 text-xs md:text-sm font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredUsers?.map(user => (
                    <tr key={user?.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-primary">
                              {getUserInitials(user?.fullName || user?.email)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm md:text-base font-medium text-foreground truncate">
                              {user?.fullName || 'Unknown'}
                              {user?.id === userProfile?.id && (
                                <span className="ml-2 text-xs text-muted-foreground">(You)</span>
                              )}
                            </div>
                            <div className="text-xs md:text-sm text-muted-foreground truncate">{user?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <span className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getRoleBadgeColor(user?.role)}`}>
                          {getRoleLabel(user?.role)}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getStatusColor(user?.isActive)}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user?.isActive ? 'bg-success' : 'bg-muted-foreground'}`}></span>
                          {user?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div className="text-sm text-muted-foreground whitespace-nowrap">
                          {user?.lastLogin ? new Date(user.lastLogin)?.toLocaleDateString() : 'Never'}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div className="flex items-center gap-2">
                          <button className="p-2 hover:bg-muted rounded-lg transition-colors" title="Edit user">
                            <Icon name="Edit2" size={16} className="text-muted-foreground" />
                          </button>
                          <button className="p-2 hover:bg-muted rounded-lg transition-colors" title="More options">
                            <Icon name="MoreVertical" size={16} className="text-muted-foreground" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers?.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground text-sm">
                        No users found matching your search
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
            <div>Showing {filteredUsers?.length} of {users?.length} users</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                <Icon name="ChevronLeft" size={16} />
              </Button>
              <Button variant="outline" size="sm" disabled>
                <Icon name="ChevronRight" size={16} />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagement;